import React from 'react';
import { API } from '../api';

/* ====================================================================
   CLUB NEWS BY EMAIL — on the login page, for people with no login.

   Subscribing needs a club, because the list belongs to one. Unsubscribing
   does not: the server emails the address one message listing every club
   that writes to it, each with its own link, so nobody has to remember which
   club they signed up with — and typing someone else's address only ever
   gets them an email.

   Opened already, with the club chosen, when the page was reached from a
   newsletter's "Subscribe to club news" link (/subscribe/:clubId).
   ==================================================================== */

const linkButton = { background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 };

/* initialClubId: from a newsletter's subscribe link — chosen, and opened.
   pageClubId: the club the login page is for — chosen, but left closed,
   because somebody who came to log in did not come to subscribe. */
function ClubNewsCard({ initialClubId, pageClubId }) {
  const [open, setOpen] = React.useState(!!initialClubId);
  const [mode, setMode] = React.useState('subscribe');
  const [clubs, setClubs] = React.useState([]);
  const [clubId, setClubId] = React.useState(String(initialClubId || pageClubId || ''));

  React.useEffect(() => {
    if (!initialClubId && pageClubId) setClubId(String(pageClubId));
  }, [initialClubId, pageClubId]);
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [website, setWebsite] = React.useState('');
  // Topic ids ticked. All of them to start with; people untick, not tick.
  const [interests, setInterests] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/mailing-list/clubs`)
      .then(r => (r.ok ? r.json() : []))
      .then((list) => {
        const safe = Array.isArray(list) ? list : [];
        setClubs(safe);
        if (safe.length === 1) setClubId(String(safe[0].id));
      })
      .catch(() => {});
  }, [open]);

  const chosenClub = clubs.find(c => String(c.id) === String(clubId));
  const topics = (chosenClub && chosenClub.topics) || [];

  React.useEffect(() => {
    setInterests(topics.map(t => t.id));
  }, [chosenClub]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTo = (m) => { setMode(m); setError(''); setDone(''); };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (mode === 'subscribe' && !clubId) { setError('Please choose a club.'); return; }
    if (mode === 'subscribe' && !interests.length) { setError('Please tick at least one thing you would like to hear about.'); return; }
    setBusy(true);
    try {
      const url = mode === 'subscribe' ? '/api/mailing-list/subscribe' : '/api/mailing-list/unsubscribe-request';
      const body = mode === 'subscribe' ? { clubId, name, email, website, interests } : { email, website };
      const r = await fetch(`${API}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
      setDone(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w3-card w3-round w3-white" style={{ marginTop: '16px' }}>
      <button type="button" className="w3-container w3-theme-d4 w3-round-top"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{ padding: '12px 20px', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ flex: 1, textAlign: 'left' }}>
          <strong><i className="fa fa-envelope w3-margin-right"></i>Club news by email</strong>
          <span className="w3-small" style={{ display: 'block', opacity: 0.85 }}>No login needed</span>
        </span>
        <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true"></i>
      </button>

      {open && (
        <div className="w3-container w3-padding-16">
          {done ? (
            <>
              <div className="w3-panel w3-pale-green w3-leftbar w3-border-green w3-round" style={{ padding: '12px 16px' }}>
                <i className="fa fa-check-circle w3-text-green w3-margin-right"></i>
                <strong>Check your email</strong>
                <p className="w3-small" style={{ margin: '8px 0 0' }}>{done}</p>
                <p className="w3-small w3-text-grey" style={{ margin: '4px 0 0' }}>Nothing changes until you click the link in that email. Check your spam folder if it hasn't arrived.</p>
              </div>
              <button type="button" className="w3-text-theme w3-small" style={linkButton}
                onClick={() => { setDone(''); setEmail(''); setName(''); }}>Done</button>
            </>
          ) : (
            <form onSubmit={submit}>
              <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
                {mode === 'subscribe'
                  ? 'Get newsletters and news from a club, whether or not you are a member.'
                  : "Enter your email and we'll send you a link to unsubscribe from each club that emails you."}
              </p>
              {error && <div className="w3-panel w3-red w3-round w3-padding-small"><small>{error}</small></div>}

              {mode === 'subscribe' && (
                <>
                  {clubs.length === 1 ? (
                    <p className="w3-small" style={{ margin: '0 0 12px' }}>Club: <strong>{clubs[0].name}</strong></p>
                  ) : (
                    <>
                      <label className="w3-small w3-text-grey" htmlFor="news-club">Club</label>
                      <select id="news-club" className="w3-select w3-border w3-round w3-margin-bottom" required
                        value={clubId} onChange={e => setClubId(e.target.value)}>
                        <option value="">Select a club...</option>
                        {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      {initialClubId && clubs.length > 0 && !chosenClub && (
                        <p className="w3-small w3-text-grey" style={{ marginTop: '-8px' }}>That club isn't taking sign-ups online. Please contact the club directly.</p>
                      )}
                    </>
                  )}
                  <label className="w3-small w3-text-grey" htmlFor="news-name">Name</label>
                  <input id="news-name" className="w3-input w3-border w3-round w3-margin-bottom" value={name}
                    onChange={e => setName(e.target.value)} placeholder="e.g. Jane Smith" maxLength={120} />
                </>
              )}

              <label className="w3-small w3-text-grey" htmlFor="news-email">Email</label>
              <input id="news-email" className="w3-input w3-border w3-round w3-margin-bottom" type="email" required
                value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" />

              {mode === 'subscribe' && topics.length > 0 && (
                <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
                  <legend className="w3-small w3-text-grey" style={{ padding: 0, marginBottom: '4px' }}>I'm interested in</legend>
                  {topics.map(t => (
                    <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', cursor: 'pointer' }}>
                      <input type="checkbox" className="w3-check" style={{ top: 0, width: 20, height: 20 }}
                        checked={interests.includes(t.id)}
                        onChange={e => setInterests(ids => (e.target.checked ? [...ids, t.id] : ids.filter(x => x !== t.id)))} />
                      <span>{t.label}</span>
                    </label>
                  ))}
                </fieldset>
              )}

              {/* No person can see this field. Anything that fills it in is a script. */}
              <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                <label htmlFor="news-website">Website</label>
                <input id="news-website" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} />
              </div>

              <button className="w3-button w3-theme w3-round w3-block" type="submit" disabled={busy}>
                {busy ? <i className="fa fa-spinner fa-spin"></i>
                  : mode === 'subscribe'
                    ? <><i className="fa fa-envelope w3-margin-right"></i>Subscribe</>
                    : <><i className="fa fa-paper-plane w3-margin-right"></i>Email me an unsubscribe link</>}
              </button>

              <p className="w3-center w3-small" style={{ margin: '12px 0 0' }}>
                {mode === 'subscribe' ? (
                  <>Want to stop club emails? <button type="button" className="w3-text-theme" style={linkButton} onClick={() => switchTo('unsubscribe')}>Unsubscribe</button></>
                ) : (
                  <>Want club news? <button type="button" className="w3-text-theme" style={linkButton} onClick={() => switchTo('subscribe')}>Subscribe</button></>
                )}
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default ClubNewsCard;
